import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BinDetail,
  changeBinStatus,
  loadBin,
  loadBinAudit
} from "./api";
import { LabeledMeasurementValue } from "./MeasurementValue";

const statuses: BinDetail["status"][] = ["ACTIVE", "BLOCKED_INBOUND", "BLOCKED_OUTBOUND", "BLOCKED_ALL", "INACTIVE"];

interface Props {
  binId: string | null;
  onClose: () => void;
}

function Metric({ label, value, unit }: {
  label: string;
  value: number | string | null;
  unit: string;
}) {
  const formatted = typeof value === "number" ? value.toLocaleString("fr-FR") : value;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography fontWeight={700}>{value == null ? "—" : `${formatted} ${unit}`}</Typography>
    </Box>
  );
}

export function BinDetailDialog({ binId, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ["bin", binId],
    queryFn: () => loadBin(binId!),
    enabled: Boolean(binId)
  });
  const audit = useQuery({
    queryKey: ["bin-audit", binId],
    queryFn: () => loadBinAudit(binId!),
    enabled: Boolean(binId)
  });
  const [nextStatus, setNextStatus] = useState<BinDetail["status"]>("ACTIVE");
  const [reasonCode, setReasonCode] = useState("");
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (detail.data) setNextStatus(detail.data.status);
  }, [detail.data]);

  const updateStatus = useMutation({
    mutationFn: () => changeBinStatus(binId!, {
      status: nextStatus,
      reasonCode,
      comment,
      version: detail.data!.version
    }),
    onSuccess: async () => {
      setReasonCode("");
      setComment("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["bin", binId] }),
        queryClient.invalidateQueries({ queryKey: ["bin-audit", binId] }),
        queryClient.invalidateQueries({ queryKey: ["warehouse-structure"] })
      ]);
    }
  });

  const bin = detail.data;
  const canSubmit = Boolean(
    bin && nextStatus !== bin.status && reasonCode.trim() && comment.trim()
  );

  return (
    <Dialog open={Boolean(binId)} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t("warehouse.bin.title", { code: bin?.code ?? "" })}</DialogTitle>
      <DialogContent>
        {detail.isLoading && <Stack spacing={1}><Skeleton /><Skeleton /><Skeleton /></Stack>}
        {detail.isError && <Alert severity="error">{t("warehouse.bin.loadError")}</Alert>}
        {bin && (
          <Stack spacing={3}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="h5" fontWeight={800}>{bin.code}</Typography>
                <Typography color="text.secondary">
                  {bin.warehouseCode} / {bin.storageAreaCode}
                  {bin.storageSectionCode ? ` / ${bin.storageSectionCode}` : ""}
                </Typography>
              </Box>
              <Chip label={t(`warehouse.bin.statuses.${bin.status}`)}
                color={bin.status === "ACTIVE" ? "success" : "warning"} />
              <Chip label={t(bin.putawayAllowed ? "warehouse.bin.putawayAllowed" : "warehouse.bin.putawayForbidden")}
                color={bin.putawayAllowed ? "success" : "error"} variant="outlined" />
              <Chip label={t(bin.pickingAllowed ? "warehouse.bin.pickingAllowed" : "warehouse.bin.pickingForbidden")}
                color={bin.pickingAllowed ? "success" : "error"} variant="outlined" />
            </Stack>

            <Divider />
            <Box>
              <Typography variant="h6" sx={{ mb: 1.5 }}>{t("warehouse.bin.sections.address")}</Typography>
              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                <Metric label={t("warehouse.bin.metrics.sequence")} value={bin.travelSequence} unit="" />
                <Metric label={t("warehouse.bin.metrics.aisle")} value={bin.aisle} unit="" />
                <Metric label={t("warehouse.bin.metrics.bay")} value={bin.bay} unit="" />
                <Metric label={t("warehouse.bin.metrics.level")} value={bin.level} unit="" />
                <Metric label={t("warehouse.bin.metrics.position")} value={bin.position} unit="" />
              </Stack>
            </Box>

            <Box>
              <Typography variant="h6" sx={{ mb: 1.5 }}>{t("warehouse.bin.sections.capacity")}</Typography>
              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                <LabeledMeasurementValue label={t("warehouse.bin.metrics.usableLength")} value={bin.usableLengthMm} kind="LENGTH" />
                <LabeledMeasurementValue label={t("warehouse.bin.metrics.usableWidth")} value={bin.usableWidthMm} kind="LENGTH" />
                <LabeledMeasurementValue label={t("warehouse.bin.metrics.usableHeight")} value={bin.usableHeightMm} kind="LENGTH" />
                <LabeledMeasurementValue label={t("warehouse.bin.metrics.opening")} value={bin.openingWidthMm} kind="LENGTH" />
                <LabeledMeasurementValue label={t("warehouse.bin.metrics.maxWeight")} value={bin.maxWeightKg} kind="MASS" />
                <LabeledMeasurementValue label={t("warehouse.bin.metrics.maxVolume")} value={bin.maxVolumeMm3} kind="VOLUME" />
              </Stack>
            </Box>

            <Box>
              <Typography variant="h6" sx={{ mb: 1.5 }}>{t("warehouse.bin.sections.occupancy")}</Typography>
              <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
                <LabeledMeasurementValue label={t("warehouse.bin.metrics.occupiedWeight")} value={bin.occupiedWeightKg} kind="MASS" />
                <LabeledMeasurementValue label={t("warehouse.bin.metrics.reservedWeight")} value={bin.reservedWeightKg} kind="MASS" />
                <LabeledMeasurementValue label={t("warehouse.bin.metrics.occupiedVolume")} value={bin.occupiedVolumeMm3} kind="VOLUME" />
                <LabeledMeasurementValue label={t("warehouse.bin.metrics.reservedVolume")} value={bin.reservedVolumeMm3} kind="VOLUME" />
              </Stack>
            </Box>

            <Divider />
            <Box>
              <Typography variant="h6" sx={{ mb: 1.5 }}>{t("warehouse.bin.sections.changeStatus")}</Typography>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel>{t("warehouse.bin.fields.newStatus")}</InputLabel>
                  <Select value={nextStatus} label={t("warehouse.bin.fields.newStatus")}
                    onChange={(event) => setNextStatus(event.target.value as BinDetail["status"])}>
                    {statuses.map((status) => (
                      <MenuItem key={status} value={status}>{t(`warehouse.bin.statuses.${status}`)}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField label={t("warehouse.bin.fields.reason")} required value={reasonCode}
                  placeholder={t("warehouse.bin.reasonPlaceholder")}
                  onChange={(event) => setReasonCode(event.target.value.toUpperCase())} />
                <TextField label={t("warehouse.bin.fields.comment")} required multiline minRows={2} value={comment}
                  onChange={(event) => setComment(event.target.value)} />
                {updateStatus.isError && (
                  <Alert severity="error">
                    {t("warehouse.bin.transitionError")}
                  </Alert>
                )}
                <Button variant="contained" disabled={!canSubmit || updateStatus.isPending}
                  onClick={() => updateStatus.mutate()}>
                  {t("warehouse.bin.apply")}
                </Button>
              </Stack>
            </Box>

            <Divider />
            <Box>
              <Typography variant="h6">{t("warehouse.bin.sections.history")}</Typography>
              {audit.data?.length === 0 && (
                <Typography color="text.secondary">{t("warehouse.bin.noHistory")}</Typography>
              )}
              <List dense>
                {audit.data?.map((entry) => (
                  <ListItem key={entry.id} disableGutters>
                    <ListItemText
                      primary={`${entry.previousStatus} → ${entry.newStatus} · ${entry.reasonCode}`}
                      secondary={`${new Date(entry.changedAt).toLocaleString(i18n.language)} — ${entry.changedBy} — ${entry.comment}`}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>{t("common.actions.close")}</Button></DialogActions>
    </Dialog>
  );
}
